// Main.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Header from '../../main/components/Header';
import MainImageSlider from '../../announcement/components/MainImageSlider';
import Description from '../../announcement/components/Description';
import Path from '../../path/ui/Path';
import MobileBottomBar from '../../main/components/MobileBottomBar';
import { postAnnouncement } from '../../../api/createannouncement';
import { Buttons } from '../../review/components/Buttons';

import getAnnouncementById from '../../../api/getAnnouncementById';

import { clearDB } from '../../../utils/indexedDBService';
import { t } from 'i18next';
import monitoringService from '../../../services/monitoringService';
import { formatPrice as formatPriceUtil } from '../../../utils/formatPrice';

const Main = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const id = location.state?.id || location.pathname.split('/').pop();

    const [isLoading, setIsLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);

    useEffect(() => {
        const fetchAnnouncementData = async () => {
            if (id) {
                // If an ID is present, fetch the announcement details
                setIsLoading(true);
                try {
                    const accessToken = localStorage.getItem("access_token");
                    const response = await getAnnouncementById(id, accessToken); // Assuming getAnnouncementById returns the full response object
                    const data = response?.announcement; // Access the 'announcement' key from the response

                    if (data) {
                        // Map fetched data to previewData format
                        setPreviewData({
                            title: data.title,
                            description: data.description,
                            videoLink: data.video_url,
                            priceType: data.negotiable ? "negotiable" : (data.barter ? "barter" : "fixed"), // Determine priceType
                            price: data.price,
                            isBarter: data.barter,
                            selectedCategory: data.category_id,
                            // You might need to fetch category name separately if not in response
                            selectedCategoryName: null, // Placeholder
                            selectedSubcategory: data.subcategory_id,
                            // You might need to fetch subcategory name separately if not in response
                            selectedSubcategoryName: null, // Placeholder
                            selectedLocation: data.location, // The location object should fit directly
                            volumeUnit: data.measurement_unit,
                            additionalPhones: data.contact_numbers?.filter(c => !c.is_user_phone).map(c => c.phone_number) || [],
                            tagsArray: data.tags || [],
                            images: [data.main_image_url, ...(data.image_urls || [])], // Combine main and additional images
                            user_name: data.seller?.name,
                            member_since: data.seller?.created_at,
                            user_rating: data.seller?.rating,
                            user_location: data.location?.area || data.location?.province || "Unknown Location",
                            views: data.views, // Ensure views are included
                            currency: data.currency, // Add currency
                            seller: data.seller, // Pass the entire seller object
                        });
                    } else {
                        console.error("Announcement not found for ID:", id);
                        navigate('/');
                    }
                } catch (error) {
                    console.error("Error fetching announcement by ID:", error);
                    navigate('/');
                } finally {
                    setIsLoading(false);
                }
            } else {
                // Existing logic for previewing from localStorage
                const storedPriceType = localStorage.getItem("priceType");
                const storedAnnouncementName = localStorage.getItem("announcementName");
                const storedVideoLink = localStorage.getItem("videoLink");
                const storedDescription = localStorage.getItem("description");
                const storedPrice = localStorage.getItem("price");
                const storedTagsArray = JSON.parse(localStorage.getItem("tagsArray") || "[]");
                const storedVolumeUnit = localStorage.getItem("volumeUnit");
                const storedSelectedLocation = JSON.parse(localStorage.getItem("selectedLocation") || "null");
                const storedSelectedCategory = localStorage.getItem("selectedCategory");
                const storedSelectedCategoryName = localStorage.getItem("selectedCategoryName");
                const storedSelectedSubcategory = localStorage.getItem("selectedSubcategory");
                const storedSelectedSubcategoryName = localStorage.getItem("selectedSubcategoryName");
                const storedIsBarter = localStorage.getItem("isBarter") === "true";

                // TODO: Implement image loading from IndexedDB here.
                // For now, let's assume `previewData.images` is an empty array or handled elsewhere.

                if (storedAnnouncementName || storedDescription || storedPrice || storedSelectedCategory) {
                    setPreviewData({
                        title: storedAnnouncementName,
                        description: storedDescription,
                        videoLink: storedVideoLink,
                        priceType: storedPriceType,
                        price: storedPrice,
                        isBarter: storedIsBarter,
                        selectedCategory: storedSelectedCategory,
                        selectedCategoryName: storedSelectedCategoryName,
                        selectedSubcategory: storedSelectedSubcategory,
                        selectedSubcategoryName: storedSelectedSubcategoryName,
                        selectedLocation: storedSelectedLocation,
                        volumeUnit: storedVolumeUnit,
                        additionalPhones: [],
                        tagsArray: storedTagsArray,
                        images: [], // Placeholder. Load from IndexedDB here.
                        user_name: "Preview User",
                        member_since: new Date().toISOString(),
                        user_rating: 0,
                        user_location: storedSelectedLocation?.address_name || "Unknown Location",
                        views: 0, // Default views for preview
                        currency: "KZT", // Default currency for preview
                        seller: { phone_number: "" }, // Placeholder for preview mode
                    });
                } else {
                    navigate(-1);
                }
            }
        };

        fetchAnnouncementData();
    }, [id, navigate]);

    if (isLoading || !previewData) {
        return (
            <div className="loading_message">
                <p>Loading announcement details...</p>
            </div>
        );
    }

    const handlePublish = async () => {
        setIsLoading(true);

        const formData = new FormData();

        formData.append("title", previewData.title || "");
        formData.append("video_url", previewData.videoLink || "");
        formData.append("description", previewData.description || "");
        formData.append("price_type", previewData.priceType || "");
        formData.append("price", previewData.priceType === "fixed" ? previewData.price : "");
        formData.append("isBarter", previewData.isBarter ? "true" : "false");

        if (previewData.selectedCategory) {
            formData.append("category_id", previewData.selectedCategory);
        } else {
            alert("Выберите категорию!");
            setIsLoading(false);
            return;
        }

        if (previewData.selectedSubcategory) {
            formData.append("subcategory_id", previewData.selectedSubcategory);
        } else {
            alert("Выберите подкатегорию!");
            setIsLoading(false);
            return;
        }

        if (previewData.selectedLocation) {
            const locId = previewData.selectedLocation.user_location_id ?? previewData.selectedLocation.address_id;
            if (locId != null && locId !== "") {
                formData.append("user_location_id", String(locId));
            } else {
                if (previewData.selectedLocation.country_id != null) {
                    formData.append("country_id", String(previewData.selectedLocation.country_id));
                }
                if (previewData.selectedLocation.region_id != null) {
                    formData.append("region_id", String(previewData.selectedLocation.region_id));
                }
                if (previewData.selectedLocation.district_id != null) {
                    formData.append("district_id", String(previewData.selectedLocation.district_id));
                }
            }
        } else {
            alert("Выберите местоположение!");
            setIsLoading(false);
            return;
        }

        if (previewData.volumeUnit) {
            const volumeUnitMap = {
                "1 Штук": "piece",
                "1 Килограм": "kilogram",
                "1 Тонна": "ton",
                "1 Литр": "liter",
                "1 Гектар": "hectare",
                "1 Шаршы метр": "square_meter",
                "1 Куб метр": "cubic_meter",
                "1 Кап": "bag",
                "1 Центнер": "centner",
                "1 Бас": "head",
                "1 Пара": "pair",
                "1 Метр": "meter",
                "1 Коробка": "box",
                // Ensure mapping for values coming from backend too
                "piece": "piece",
                "kilogram": "kilogram",
                "ton": "ton",
                "liter": "liter",
                "hectare": "hectare",
                "square_meter": "square_meter",
                "cubic_meter": "cubic_meter",
                "bag": "bag",
                "centner": "centner",
                "head": "head",
                "pair": "pair",
                "meter": "meter",
                "box": "box"
            };

            const convertedVolumeUnit = volumeUnitMap[previewData.volumeUnit] || previewData.volumeUnit;
            formData.append("measurement_unit", convertedVolumeUnit);
        } else {
            alert("Выберите единицу измерения!");
            setIsLoading(false);
            return;
        }

        if (Array.isArray(previewData.additionalPhones)) {
            previewData.additionalPhones.forEach((phone, index) => {
                formData.append(`additionalPhones[${index}]`, phone);
            });
        }

        if (Array.isArray(previewData.tagsArray)) {
            previewData.tagsArray.forEach(tag => {
                formData.append("tags[]", tag.trim());
            });
        }

        async function urlToBlob(url) {
            const response = await fetch(url);
            const blob = await response.blob();
            return new File([blob], "image.jpg", { type: blob.type });
        }

        if (Array.isArray(previewData.images)) {
            const imageFiles = await Promise.all(previewData.images.map(async (file) => {
                if (typeof file === "string") {
                    return urlToBlob(file);
                }
                return file;
            }));

            imageFiles.forEach(file => formData.append("images", file));
        }

        try {
            const accessToken = localStorage.getItem("access_token");
            const response = await postAnnouncement(formData, accessToken);

            if (response?.announcement_id) {
                localStorage.removeItem("priceType");
                localStorage.removeItem("announcementName");
                localStorage.removeItem("videoLink");
                localStorage.removeItem("description");
                localStorage.removeItem("price");
                localStorage.removeItem("tagsArray");
                localStorage.removeItem("volumeUnit");
                localStorage.removeItem("selectedLocation");
                localStorage.removeItem("selectedCategory");
                localStorage.removeItem("selectedCategoryName");
                localStorage.removeItem("selectedSubcategory");
                localStorage.removeItem("selectedSubcategoryName");
                clearDB("savedImages");
                clearDB("savedImageFiles");
                
                // Track successful advertisement creation
                monitoringService.trackAdvertise(
                    response.announcement_id,
                    true,
                    null,
                    {
                        categoryId: previewData.selectedCategory,
                        subcategoryId: previewData.selectedSubcategory,
                        priceType: previewData.priceType,
                        price: previewData.price,
                        isBarter: previewData.isBarter,
                    }
                ).catch(err => {
                    console.warn('Failed to track advertisement:', err);
                });
                
                navigate(`/announcement/${response.announcement_id}`);
            } else {
                const errorMsg = "Ошибка: ID объявления отсутствует в ответе.";
                console.error(errorMsg);
                alert(errorMsg);
                // Track failed advertisement creation
                monitoringService.trackAdvertise(null, false, errorMsg).catch(err => {
                    console.warn('Failed to track advertisement failure:', err);
                });
            }
        } catch (error) {
            const errorMsg = error.message || "Ошибка при создании объявления. Пожалуйста, попробуйте снова.";
            console.error("Error creating announcement:", error);
            alert(errorMsg);
            // Track failed advertisement creation
            monitoringService.trackAdvertise(null, false, errorMsg).catch(err => {
                console.warn('Failed to track advertisement failure:', err);
            });
        } finally {
            setIsLoading(false);
        }
    };

    const {
        title,
        description,
        price,
        priceType,
        images,
        videoLink,
        user_name,
        member_since,
        user_rating,
        user_location,
        volumeUnit,
        isBarter,
        seller
    } = previewData;

    const user = {
        name: user_name,
        created_at: member_since,
        rating: user_rating,
    };

    return (
        <div className="main_container">
            <Header />
            <Path currentPath={t('profile')} />
            <div className="announcement_content">
                <div className="main_content">
                    <MainImageSlider
                        id={id || "preview"}
                        images={Array.isArray(images) && images.length > 0 ? images : []}
                        description={description || "No description available"}
                        views={previewData.views || 0}
                        videoUrl={videoLink || null}
                        isLoading={isLoading}
                    />
                    {!id && (
                        <Buttons
                            handlePublish={handlePublish}
                            isLoading={isLoading}
                            isMobile={'desktop'}
                        />
                    )}
                    <div className="main_details">
                        <Description
                            id={id}
                            title={title || "Untitled"}
                            date={seller?.created_at || new Date().toISOString()}
                            price={formatPriceUtil(price, priceType, null, isBarter)}
                            user={user}
                            location={user_location}
                            unit={volumeUnit}
                            isBarter={isBarter}
                            priceType={priceType}
                            phone={seller?.phone_number}
                            rating={user_rating}
                            reviews={seller?.reviews_count}
                            user_id={seller?.id}
                        />
                    </div>
                </div>
                <MobileBottomBar />
                {!id && (
                    <Buttons
                        handlePublish={handlePublish}
                        isLoading={isLoading}
                        isMobile={'mobile'}
                    />
                )}
            </div>
        </div>
    );
};

export default Main;